import { useEffect, useState } from "react";
import api from "../utils/axios";
import formatCurrency from "../helper/formatCurrency";

interface DepositItem {
  transaction_id: number;
  uid: string;
  name: string;
  amount: number;
  status: string;
  transaction_date: string;

  payment_type: "crypto" | "bank" | "online";

  paymentmethod: string;
  transaction_type?: string;

  utr_no: string | null;
  txhash: string | null;
  wallet_address: string | null;
  transactionid: string | null;

  receipt?: string;

  totalbonuspercentage?: number;
}

const getReceiptUrl = (receipt: string) => {
  if (!receipt) return "";
  if (receipt.startsWith("http://") || receipt.startsWith("https://")) {
    return receipt;
  }
  const cleanPath = receipt.startsWith("/") ? receipt.slice(1) : receipt;
  const baseUrl = "https://api.pori444.com";
  return cleanPath.startsWith("uploads/")
    ? `${baseUrl}/${cleanPath}`
    : `${baseUrl}/uploads/${cleanPath}`;
};

const ManualDeposit = () => {
  const [tab, setTab] = useState<"crypto" | "bank" | "online">("bank");
  const [data, setData] = useState<DepositItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchData();
  }, [tab, page]);

  const fetchData = async () => {
    const res = await api.get(`/getManualDepositData?page=${page}&limit=10`, {
      params: { payment_type: tab },
      headers: { Authorization: token },
    });

    setData(res?.data?.data || []);
    setTotalPages(res?.data?.totalPages || 1);
  };

  const handleAction = async (d: DepositItem, isApproved: boolean) => {
    await api.post(
      "/approveRejectManualDeposit",
      {
        trx: d,
        isApproved,
      },
      { headers: { Authorization: token } }
    );

    fetchData();
  };

  return (
    <div className="space-y-6">
      {/* ================= HEADER ================= */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Manual Deposits</h1>
        <p className="text-sm text-gray-500">
          Manage Bank (bKash, Nagad, Rocket, Upay), Online & Crypto deposit approvals
        </p>
      </div>

      {/* ================= TABS ================= */}
      <div className="flex gap-2">
        {["bank", "online", "crypto"].map((t) => (
          <button
            key={t}
            onClick={() => {
              setTab(t as "crypto" | "bank" | "online");
              setPage(1);
            }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === t
                ? "bg-gray-900 text-white shadow-sm"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {t === "crypto"
              ? "Crypto Deposits"
              : t === "online"
              ? "Online Deposits"
              : "Bank Deposits (bKash/Nagad/Rocket/Upay)"}
          </button>
        ))}
      </div>

      {/* ================= TABLE ================= */}
      <div className="bg-white border rounded-2xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-4 py-3 text-left">#</th>
              <th className="px-4 py-3 text-left">User</th>
              <th className="px-4 py-3 text-left">Amount</th>
              <th className="px-4 py-3 text-left">Bonus</th>

              {tab === "crypto" ? (
                <>
                  <th className="px-4 py-3 text-left">Network</th>
                  <th className="px-4 py-3 text-left">TxHash</th>
                  <th className="px-4 py-3 text-left">Wallet</th>
                </>
              ) : (
                <>
                  <th className="px-4 py-3 text-left">Gateway</th>
                  <th className="px-4 py-3 text-left">TrxID / UTR</th>
                </>
              )}

              <th className="px-4 py-3 text-center">Receipt</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-left">Action</th>
            </tr>
          </thead>

          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={10} className="py-10 text-center text-gray-500">
                  No records found
                </td>
              </tr>
            ) : (
              data.map((d, i) => (
                <tr
                  key={d.transaction_id}
                  className="border-t hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 py-3">{(page - 1) * 10 + i + 1}</td>

                  <td className="px-4 py-3 font-medium">
                    {d.name || d.uid}
                  </td>

                  {/* AMOUNT */}
                  <td className="px-4 py-3 font-semibold text-gray-900">
                    {tab === "crypto" ? `${d.amount} USDT` : `৳${d.amount.toFixed(2)}`}
                  </td>

                  <td className="px-4 py-3 text-emerald-600 font-medium">
                    {d.totalbonuspercentage
                      ? tab === "crypto"
                        ? `${((d.amount * d.totalbonuspercentage) / 100).toFixed(2)} USDT (${d.totalbonuspercentage}%)`
                        : `৳${((d.amount * d.totalbonuspercentage) / 100).toFixed(2)} (${d.totalbonuspercentage}%)`
                      : tab === "crypto"
                      ? "0.00 USDT"
                      : "৳0.00"}
                  </td>

                  {/* GATEWAY / UTR */}
                  {tab === "crypto" ? (
                    <>
                      <td className="px-4 py-3">
                        <span className="px-2.5 py-1 rounded-md text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          {d.paymentmethod || "USDT (TRC20)"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs font-mono select-all text-gray-700 max-w-[150px] truncate" title={d.txhash || ""}>
                        {d.txhash || "-"}
                      </td>
                      <td className="px-4 py-3 text-xs font-mono select-all text-gray-700 max-w-[150px] truncate" title={d.wallet_address || ""}>
                        {d.wallet_address || "-"}
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-3">
                        <span className="px-2.5 py-1 rounded-md text-xs font-bold bg-gray-100 text-gray-800 border border-gray-200">
                          {d.paymentmethod || d.payment_type || "-"}
                        </span>
                      </td>

                      <td className="px-4 py-3 text-xs font-mono font-semibold text-gray-800">
                        {d.transactionid || d.utr_no || "-"}
                      </td>
                    </>
                  )}

                  {/* RECEIPT */}
                  <td className="px-4 py-3 text-center">
                    {d.receipt ? (
                      <div className="flex items-center justify-center gap-2">
                        <img
                          src={getReceiptUrl(d.receipt)}
                          alt="Receipt"
                          className="h-10 w-10 object-cover rounded-lg border border-gray-200 cursor-pointer hover:scale-105 transition-transform shadow-xs"
                          onClick={() => setSelectedImage(getReceiptUrl(d.receipt!))}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = "none";
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setSelectedImage(getReceiptUrl(d.receipt!))}
                          className="text-blue-600 hover:text-blue-800 text-xs font-semibold underline"
                        >
                          View
                        </button>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>

                  {/* STATUS */}
                  <td className="px-4 py-3">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        d.status === "pending"
                          ? "bg-yellow-100 text-yellow-700 border border-yellow-200"
                          : d.status === "accepted"
                          ? "bg-green-100 text-green-700 border border-green-200"
                          : "bg-red-100 text-red-700 border border-red-200"
                      }`}
                    >
                      {d.status}
                    </span>
                  </td>

                  {/* DATE */}
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {new Date(d.transaction_date).toLocaleString()}
                  </td>

                  {/* ACTION */}
                  <td className="px-4 py-3">
                    {d.status === "pending" ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleAction(d, true)}
                          className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-semibold shadow-xs transition-colors"
                        >
                          Accept
                        </button>

                        <button
                          onClick={() => handleAction(d, false)}
                          className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-semibold shadow-xs transition-colors"
                        >
                          Reject
                        </button>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* ================= PAGINATION ================= */}
        {totalPages > 1 && (
          <div className="p-4 border-t flex justify-between items-center text-sm">
            <span className="text-gray-500">
              Page {page} of {totalPages}
            </span>

            <div className="flex gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1 border rounded-lg disabled:opacity-40 hover:bg-gray-50"
              >
                Previous
              </button>

              <button
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 border rounded-lg disabled:opacity-40 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ================= RECEIPT MODAL ================= */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-xs p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="bg-white rounded-2xl p-5 max-w-lg w-full shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3 border-b pb-2">
              <h3 className="text-base font-bold text-gray-900">Payment Receipt</h3>
              <button
                type="button"
                onClick={() => setSelectedImage(null)}
                className="text-gray-400 hover:text-gray-700 text-xl font-bold px-2"
              >
                ✕
              </button>
            </div>

            <div className="bg-gray-50 border rounded-xl p-2 flex items-center justify-center max-h-[70vh] overflow-auto">
              <img
                src={selectedImage}
                alt="Full Receipt"
                className="max-w-full max-h-[65vh] object-contain rounded-lg shadow-xs"
              />
            </div>

            <div className="mt-4 flex justify-between items-center">
              <a
                href={selectedImage}
                target="_blank"
                rel="noreferrer"
                className="text-xs font-semibold text-blue-600 hover:underline"
              >
                Open Original in New Tab ↗
              </a>
              <button
                type="button"
                onClick={() => setSelectedImage(null)}
                className="px-4 py-1.5 bg-gray-900 hover:bg-gray-800 text-white text-xs font-semibold rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualDeposit;